import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';

import { useAuth } from '../../../context/AuthContext';
import {
  getProjectVitalAccessContext,
  type ProjectVitalAccessContext,
} from '../services/projectVitalService';

export default function ProjectVitalProtectedRoute() {
  const { user, isLoading, signOut } = useAuth();
  const location = useLocation();

  const userId = user?.id ?? null;

  const [accessContext, setAccessContext] =
    useState<ProjectVitalAccessContext | null>(null);

  const [hasCheckedAccess, setHasCheckedAccess] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadAccessContext() {
      if (!userId) {
        setAccessContext(null);
        setHasCheckedAccess(true);
        return;
      }

      setAccessError(null);

      try {
        const context = await getProjectVitalAccessContext();

        if (!isMounted) {
          return;
        }

        setAccessContext(context);
      } catch (error) {
        console.error('[ProjectVital] Access context error:', error);

        if (!isMounted) {
          return;
        }

        setAccessContext(null);
        setAccessError(
          'No se pudo comprobar el acceso a Proyecto Vital.'
        );
      } finally {
        if (isMounted) {
          setHasCheckedAccess(true);
        }
      }
    }

    if (!isLoading) {
      setHasCheckedAccess(false);
      void loadAccessContext();
    }

    return () => {
      isMounted = false;
    };
  }, [isLoading, userId]);

  if (isLoading || !hasCheckedAccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/project-vital/login"
        state={{ from: location }}
        replace
      />
    );
  }

  if (
    accessError ||
    !accessContext?.has_access ||
    accessContext.user_id !== userId
  ) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          px: 2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            width: '100%',
            maxWidth: 520,
            p: {
              xs: 3,
              sm: 4,
            },
            borderRadius: 3,
          }}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography
                component="h1"
                variant="h5"
                sx={{ fontWeight: 800 }}
              >
                Proyecto Vital
              </Typography>

              <Typography
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Tu sesión es válida, pero esta cuenta no tiene acceso
                habilitado a Proyecto Vital.
              </Typography>
            </Box>

            {accessError && (
              <Alert severity="warning">
                {accessError}
              </Alert>
            )}

            <Button
              variant="outlined"
              onClick={() => void signOut()}
            >
              Cerrar sesión
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return <Outlet />;
}
